import React, { useEffect, useRef, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Button,
  Box,
  Typography,
  Divider,
  CircularProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import { renderAsync } from "docx-preview";

// Stiahne blob v prehliadači (volá sa len na vyžiadanie z popupu)
const triggerDownload = (blob, filename) => {
  if (!blob) return;
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename || "subor";
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
};

// Náhľad výsledného (anonymizovaného / pseudonymizovaného) súboru v okne.
// PDF sa zobrazí cez vstavaný PDF viewer prehliadača (iframe), DOCX cez docx-preview.
const ValidationResultModal = ({ open, result, onClose }) => {
  const docxRef = useRef(null);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [renderError, setRenderError] = useState(null);

  const blob = result?.blob || null;
  const filename = result?.filename || "vysledok";
  const isDocx = (filename || "").toLowerCase().endsWith(".docx");
  const isPdf = (filename || "").toLowerCase().endsWith(".pdf");

  // PDF -> object URL pre iframe
  useEffect(() => {
    if (!open || !blob || !isPdf) {
      setPdfUrl(null);
      return undefined;
    }
    const url = window.URL.createObjectURL(blob);
    setPdfUrl(url);
    return () => window.URL.revokeObjectURL(url);
  }, [open, blob, isPdf]);

  // DOCX -> render cez docx-preview
  useEffect(() => {
    let cancelled = false;
    const renderDocx = async () => {
      if (!open || !blob || !isDocx || !docxRef.current) return;
      setLoading(true);
      setRenderError(null);
      try {
        docxRef.current.innerHTML = "";
        await renderAsync(blob, docxRef.current, null, {
          className: "docx-viewer",
          inWrapper: true,
        });
      } catch (err) {
        if (!cancelled) setRenderError(err?.message || "Náhľad sa nepodarilo načítať.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    const timer = setTimeout(renderDocx, 100);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [open, blob, isDocx]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{ sx: { borderRadius: 3 } }}
    >
      <DialogTitle
        sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", py: 2 }}
      >
        <Typography variant="h6" sx={{ fontWeight: 600 }} noWrap>
          {filename}
        </Typography>
        <IconButton onClick={onClose} size="small" sx={{ bgcolor: "action.hover", color: "text.secondary" }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <Divider />

      <DialogContent dividers sx={{ bgcolor: "background.default", p: 0 }}>
        {!blob && (
          <Box sx={{ p: 4, textAlign: "center", height: "60vh", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <Typography color="text.secondary">Žiadny súbor na zobrazenie.</Typography>
          </Box>
        )}

        {blob && isPdf && pdfUrl && (
          <Box sx={{ height: "80vh", width: "100%" }}>
            <iframe
              title={filename}
              src={pdfUrl}
              style={{ width: "100%", height: "100%", border: "none" }}
            />
          </Box>
        )}

        {blob && isDocx && (
          <>
            {loading && (
              <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "60vh" }}>
                <CircularProgress />
              </Box>
            )}
            {renderError && (
              <Box sx={{ p: 4, textAlign: "center", height: "60vh", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <Typography color="error">Chyba pri načítaní náhľadu: {renderError}</Typography>
              </Box>
            )}
            <Box
              ref={docxRef}
              sx={{
                height: "80vh",
                overflow: "auto",
                display: loading || renderError ? "none" : "block",
                p: { xs: 2, md: 4 },
                "& .docx-viewer": {
                  margin: "0 auto",
                  backgroundColor: "#fff",
                  boxShadow: 3,
                  borderRadius: 1,
                  overflow: "hidden",
                  color: "#000",
                },
              }}
            />
          </>
        )}

        {blob && !isPdf && !isDocx && (
          <Box sx={{ p: 4, textAlign: "center", height: "40vh", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <Typography color="text.secondary">
              Náhľad tohto typu súboru nie je dostupný. Súbor si môžete stiahnuť tlačidlom nižšie.
            </Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
        <Button onClick={onClose} color="inherit" sx={{ borderRadius: 2, fontWeight: 600 }}>
          Zavrieť
        </Button>
        <Box sx={{ flex: 1 }} />
        <Button
          onClick={() => triggerDownload(blob, filename)}
          disabled={!blob}
          variant="contained"
          color="primary"
          disableElevation
          startIcon={<DownloadOutlinedIcon />}
          sx={{ borderRadius: 2, fontWeight: 600 }}
        >
          Stiahnuť súbor
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ValidationResultModal;
